import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Text,
  Grid,
  Image,
  Button,
  Flex,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  useToast,
  useBreakpointValue,
  Stack
} from '@chakra-ui/react';
import { getGalleryByToken, purchaseGalleryAccess } from '../lib/galleryService';
import type { ClientGallery } from '../lib/supabase';
import PayPalButton from '../PayPalButton';
import JSZip from 'jszip';

// Using ClientGallery type from supabase.ts

const ClientGalleryPage: React.FC = () => {
  const { token, lng } = useParams<{ token: string; lng?: string }>();
  const { t } = useTranslation('gallery');
  const currentLanguage = lng || 'en';
  const [gallery, setGallery] = useState<ClientGallery | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clientEmail, setClientEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const toast = useToast();

  // Responsive values
  const gridColumns = useBreakpointValue({ 
    base: "repeat(1, 1fr)", 
    sm: "repeat(2, 1fr)", 
    md: "repeat(3, 1fr)", 
    lg: "repeat(auto-fit, minmax(300px, 1fr))" 
  });
  const maxImageHeight = useBreakpointValue({ base: "300px", md: "400px" });
  const containerPadding = useBreakpointValue({ base: 4, md: 8 });
  const stackDirection = useBreakpointValue({ base: "column", md: "row" }) as "column" | "row";

  useEffect(() => {
    if (token) {
      loadGalleryConfig(token);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGalleryConfig = async (galleryToken: string) => {
    try {
      const galleryData = await getGalleryByToken(galleryToken);
      if (galleryData) {
        setGallery(galleryData);
      } else {
        setError(t('notFound'));
      }
    } catch (err) {
      setError('Gallery not found or has expired');
    } finally {
      setLoading(false);
    }
  };

  // No longer need to reload on email change since access is independent of email

  const handleFreeAccess = async () => {
    if (!token) return;

    setIsProcessing(true);

    try {
      const result = await purchaseGalleryAccess(token, 'anonymous@free.access', undefined, currentLanguage);
      
      if (result.success) {
        toast({
          title: t('purchaseComplete'),
          description: t('accessActivated'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Reload gallery to show access
        await loadGalleryConfig(token);
      } else {
        throw new Error(result.error || 'Failed to process access');
      }
    } catch (error) {
      toast({
        title: t('paymentFailed'),
        description: error instanceof Error ? error.message : t('paymentFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayPalSuccess = async (paymentDetails: any) => {
    if (!token) {
      console.error('No gallery token available');
      return;
    }

    console.log('PayPal payment successful, processing...', paymentDetails);
    setIsProcessing(true);

    try {
      // Use email if provided, otherwise use a generic payment email
      const emailForPurchase = clientEmail || 'payment@gallery.purchase';
      console.log('Processing purchase for email:', emailForPurchase);
      
      const result = await purchaseGalleryAccess(token, emailForPurchase, paymentDetails, currentLanguage);
      console.log('Purchase result:', result);
      
      if (result.success) {
        toast({
          title: t('paymentSuccessful'),
          description: t('accessActivated'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        console.log('Reloading gallery to show access...');
        // Reload gallery to show access and high-res images
        await loadGalleryConfig(token);
        console.log('Gallery reloaded successfully');
      } else {
        console.error('Purchase failed:', result.error);
        throw new Error(result.error || 'Failed to process purchase');
      }
    } catch (error) {
      console.error('PayPal payment error:', error);
      toast({
        title: t('paymentFailed'),
        description: error instanceof Error ? error.message : t('paymentFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      console.log('Payment processing complete, clearing loading state');
      setIsProcessing(false);
    }
  };

  const getCleanFilename = (image: any, index?: number) => {
    if (image.filename) {
      // Remove "_preview" and token prefix, keep original extension
      let cleanName = image.filename
        .replace(/_preview\.jpg$/, '') // Remove "_preview.jpg"
        .replace(/^[^_]+_\d+_\d+_/, '') // Remove token_index_timestamp prefix
        .replace(/^original\./, ''); // Remove "original." prefix if present
      
      // If the filename is still not clean, create a descriptive one
      if (!cleanName || cleanName.length < 3) {
        cleanName = `${gallery?.title || 'Gallery'}_Image_${(index || 0) + 1}`;
      }
      
      // Ensure it has an extension
      if (!cleanName.includes('.')) {
        cleanName += '.jpg';
      }
      
      return cleanName;
    }
    
    return `${gallery?.title || 'Gallery'}_Image_${(index || 0) + 1}.jpg`;
  };

  const handleDownloadSingleImage = async (image: any) => {
    if (!image.originalUrl) {
      toast({
        title: t('downloadFailed'),
        description: t('downloadFailed'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      console.log(`Attempting to download image: ${image.filename}, URL: ${image.originalUrl}`);
      
      // Show loading state
      toast({
        title: t('preparingDownload'),
        description: `Fetching ${image.filename || 'image'}`,
        status: 'info',
        duration: 2000,
        isClosable: true,
      });

      // Simple approach: Use the direct S3 URL 
      const downloadUrl = image.originalUrl;
      const cleanFilename = getCleanFilename(image);
      
      console.log(`Direct download from: ${downloadUrl}`);

      // Directly download the file using the S3 URL
      const response = await fetch(downloadUrl, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      });
      
      console.log(`Download response status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = cleanFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(blobUrl);

      toast({
        title: t('downloadStarted'),
        description: `${cleanFilename} has been downloaded`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Single image download error:', error);
      
      // More specific error messaging
      let errorDescription = t('downloadFailed');
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorDescription = 'Network error or invalid download URL. The image may not be available.';
        } else if (error.message.includes('404')) {
          errorDescription = 'Image not found. It may have been removed or is temporarily unavailable.';
        } else if (error.message.includes('403')) {
          errorDescription = 'Access denied. The download link may have expired.';
        } else {
          errorDescription = `Download failed: ${error.message}`;
        }
      }
      
      toast({
        title: t('downloadFailed'),
        description: errorDescription,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDownloadAllImages = async () => {
    if (!gallery?.hasAccess || !gallery.images.length) {
      toast({
        title: 'Access required',
        description: 'Please purchase gallery access to download images.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsProcessing(true);
      
      const zip = new JSZip();
      const imageFolder = zip.folder(gallery.title || 'Gallery');
      
      toast({
        title: 'Preparing download...',
        description: `Creating zip file with ${gallery.images.length} images`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });

      // Download all images and add to zip
      const downloadPromises = gallery.images.map(async (image, index) => {
        if (!image.originalUrl) {
          console.warn(`No original URL for image ${image.filename}`);
          return;
        }
        
        try {
          console.log(`Downloading ${image.filename} from:`, image.originalUrl);
          
          // Simple approach: Use the direct S3 URL
          const response = await fetch(image.originalUrl);
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const blob = await response.blob();
          console.log(`Downloaded ${image.filename}: ${blob.size} bytes, type: ${blob.type}`);
          
          const cleanFileName = getCleanFilename(image, index);
          imageFolder?.file(cleanFileName, blob);
        } catch (error) {
          console.error(`Failed to download ${image.filename}:`, error);
          const cleanFileName = getCleanFilename(image, index);
          toast({
            title: `Failed to download ${cleanFileName}`,
            description: error instanceof Error ? error.message : 'Unknown error',
            status: 'warning',
            duration: 2000,
            isClosable: true,
          });
        }
      });

      await Promise.all(downloadPromises);

      // Generate and download zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipUrl = URL.createObjectURL(zipBlob);
      
      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `${gallery.title || 'Gallery'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(zipUrl);

      toast({
        title: 'Download complete!',
        description: `${gallery.title}.zip has been downloaded`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Download failed',
        description: 'Failed to create zip file. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePayPalError = (error: any) => {
    console.error('PayPal error:', error);
    toast({
      title: t('paymentFailed'),
      description: t('paymentFailed'),
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minH="50vh">
        <Spinner size="xl" />
        <Text mt={4}>{t('loading')}</Text>
      </Box>
    );
  }

  if (error || !gallery) {
    return (
      <Box maxW="container.md" mx="auto" p={8}>
        <Alert status="error">
          <AlertIcon />
          {error || t('notFound')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box maxW="container.xl" mx="auto" p={containerPadding} paddingTop={{ base: "80px", md: "20px" }}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" mb={2}>
            {gallery.title}
          </Text>
          <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" mb={2}>
            {t('greeting', { clientName: gallery.clientName })}
          </Text>
          
          {/* Expiration notice */}
          {gallery.expiresAt && gallery.daysUntilExpiry !== undefined && (
            <Box 
              bg={gallery.daysUntilExpiry <= 3 ? "red.50" : gallery.daysUntilExpiry <= 7 ? "orange.50" : "blue.50"}
              border="1px solid" 
              borderColor={gallery.daysUntilExpiry <= 3 ? "red.200" : gallery.daysUntilExpiry <= 7 ? "orange.200" : "blue.200"}
              borderRadius="lg" 
              p={3} 
              mb={4}
              maxW="500px"
              mx="auto"
            >
              <Text 
                fontSize={{ base: "sm", md: "md" }} 
                color={gallery.daysUntilExpiry <= 3 ? "red.800" : gallery.daysUntilExpiry <= 7 ? "orange.800" : "blue.800"}
                fontWeight="medium"
              >
                {gallery.daysUntilExpiry <= 0 
                  ? "⏰ " + t('expired')
                  : `⏰ ${t('expiresIn', {days: gallery.daysUntilExpiry})}`
                }
              </Text>
              <Text 
                fontSize="xs" 
                color={gallery.daysUntilExpiry <= 3 ? "red.600" : gallery.daysUntilExpiry <= 7 ? "orange.600" : "blue.600"}
                mt={1}
              >
{t('expiresOn', {date: new Date(gallery.expiresAt).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })})}
              </Text>
            </Box>
          )}
          
          {/* Prints promotion */}
          <Box 
            bg="blue.50" 
            border="1px solid" 
            borderColor="blue.200" 
            borderRadius="lg" 
            p={{ base: 4, md: 6 }} 
            mb={6}
            maxW="600px"
            mx="auto"
          >
            <Text fontSize={{ base: "sm", md: "md" }} color="blue.800" fontWeight="medium">
              {t('printsAvailableTitle')}
            </Text>
            <Text fontSize={{ base: "sm", md: "md" }} color="blue.700" mt={2}>
              {t('printsAvailableDescription')}
            </Text>
          </Box>
        </Box>

        {gallery.hasAccess ? (
          // Full gallery grid when user has access
          <Grid templateColumns={gridColumns} gap={{ base: 4, md: 6 }}>
            {gallery.images.map(image => (
              <Box key={image.id} position="relative">
                <Box
                  position="relative"
                  border="2px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                  transition="all 0.2s"
                  _hover={{ borderColor: "blue.300" }}
                >
                  <Image
                    src={image.previewUrl}
                    alt=""
                    w="100%"
                    maxH={maxImageHeight}
                    objectFit="contain"
                    bg="gray.50"
                  />
                  
                  {/* Individual download button */}
                  <Button
                    position="absolute"
                    top={3}
                    right={3}
                    size="sm"
                    colorScheme="green"
                    onClick={() => handleDownloadSingleImage(image)}
                    leftIcon={<Box fontSize="xs">⬇️</Box>}
                  >
                    {t('downloadSingle')}
                  </Button>
                </Box>
              </Box>
            ))}
          </Grid>
        ) : (
          // Preview stack when user doesn't have access
          <Box textAlign="center">
            <VStack spacing={6}>
              {/* Stacked Cards Preview */}
              <Box position="relative" width="fit-content" mx="auto">
                {gallery.images.length === 1 ? (
                  // Single image preview
                  <Box
                    position="relative"
                    border="3px solid"
                    borderColor="gray.300"
                    borderRadius="lg"
                    overflow="hidden"
                    shadow="lg"
                    maxW="400px"
                    mx="auto"
                  >
                    <Image
                      src={gallery.images[0].previewUrl}
                      alt=""
                      w="100%"
                      maxH="300px"
                      objectFit="contain"
                      bg="gray.50"
                    />
                    
                    {/* Watermark overlay */}
                    <Box
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      color="white"
                      fontSize="xl"
                      fontWeight="bold"
                      textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                      pointerEvents="none"
                      opacity={0.7}
                    >
                      © Pablo Ledesma
                    </Box>

                    {/* Access required overlay */}
                    <Box
                      position="absolute"
                      bottom={3}
                      left={3}
                      bg="blackAlpha.700"
                      color="white"
                      px={3}
                      py={2}
                      borderRadius="md"
                      fontSize="sm"
                      fontWeight="bold"
                    >
{t('accessRequiredOverlay')}
                    </Box>
                  </Box>
                ) : (
                  // Stacked cards for multiple images
                  <Box position="relative" width="300px" height="250px">
                    {gallery.images.slice(0, 3).map((image, index) => (
                      <Box
                        key={image.id}
                        position="absolute"
                        border="2px solid"
                        borderColor="white"
                        borderRadius="lg"
                        overflow="hidden"
                        shadow="lg"
                        bg="white"
                        transform={`rotate(${(index - 1) * 3}deg) translateX(${index * 8}px) translateY(${index * 6}px)`}
                        zIndex={3 - index}
                        w="280px"
                        h="200px"
                      >
                        <Image
                          src={image.previewUrl}
                          alt=""
                          w="100%"
                          h="100%"
                          objectFit="cover"
                          bg="gray.50"
                        />
                        
                        {/* Watermark overlay */}
                        <Box
                          position="absolute"
                          top="50%"
                          left="50%"
                          transform="translate(-50%, -50%)"
                          color="white"
                          fontSize="lg"
                          fontWeight="bold"
                          textShadow="2px 2px 4px rgba(0,0,0,0.8)"
                          pointerEvents="none"
                          opacity={0.6}
                        >
                          © Pablo Ledesma
                        </Box>
                      </Box>
                    ))}
                    
                    {/* Image counter badge */}
                    <Box
                      position="absolute"
                      top={-2}
                      right={-2}
                      bg="blue.500"
                      color="white"
                      borderRadius="full"
                      w="60px"
                      h="60px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="sm"
                      fontWeight="bold"
                      shadow="lg"
                      zIndex={10}
                    >
                      {gallery.images.length}
                      <br />
{gallery.images.length === 1 ? t('image') : t('images')}
                    </Box>
                  </Box>
                )}
              </Box>

              {/* Preview description */}
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="bold" color="gray.800">
                  {gallery.images.length === 1 
                    ? t('preview') 
                    : t('totalImages') + `: ${gallery.images.length}`
                  }
                </Text>
                <Text fontSize="md" color="gray.600">
                  {gallery.images.length === 1
                    ? t('purchaseForSingle')
                    : t('purchaseForMultiple', {count: gallery.images.length})
                  }
                </Text>
              </VStack>
            </VStack>
          </Box>
        )}

        {/* Gallery Access Purchase Section */}
        {!gallery.hasAccess && (
          <Box
            position="sticky"
            bottom={0}
            bg="white"
            borderTop="2px solid"
            borderColor="gray.200"
            p={6}
            shadow="lg"
          >
            <VStack spacing={4}>
              <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" textAlign="center">
                {t('purchaseAccess')}
              </Text>
              
              <Text fontSize={{ base: "md", md: "lg" }} color="gray.600" textAlign="center">
                €{gallery.pricePerImage} for complete gallery access
              </Text>

              {gallery.pricePerImage > 0 && (
                <Stack direction={stackDirection} spacing={{ base: 4, md: 8 }} w="100%" maxW="500px">
                  <FormControl>
                    <FormLabel>{t('enterEmail')}</FormLabel>
                    <Input
                      type="email"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </FormControl>
                </Stack>
              )}
              
              <Flex 
                justify="center" 
                align="center" 
                w="100%" 
                direction={{ base: "column", md: "row" }}
                gap={{ base: 4, md: 4 }}
              >
                {gallery.pricePerImage === 0 ? (
                  <Button 
                    onClick={handleFreeAccess} 
                    colorScheme="green" 
                    size="lg"
                    px={8}
                    isLoading={isProcessing}
                    loadingText={t('processing')}
                  >
{t('free')} {t('purchaseAccess')}
                  </Button>
                ) : (
                  <>
                    {!clientEmail ? (
                      <Text color="orange.500" textAlign="center" fontSize="sm">
                        {t('emailRequired')}
                      </Text>
                    ) : (
                      <PayPalButton
                        price={gallery.pricePerImage}
                        onSuccess={handlePayPalSuccess}
                        onError={handlePayPalError}
                      />
                    )}
                  </>
                )}
              </Flex>
            </VStack>
          </Box>
        )}

        {/* Success Message with Download Button - Show when has access */}
        {gallery.hasAccess && (
          <Box
            bg="green.50"
            border="2px solid"
            borderColor="green.200"
            borderRadius="lg"
            p={6}
            textAlign="center"
          >
            <VStack spacing={4}>
              <Text fontSize="lg" fontWeight="bold" color="green.800">
                ✅ {t('accessGranted')}
              </Text>
              <Text color="green.700">
                {t('downloadInstructions')}
              </Text>
              <Button
                colorScheme="green"
                size="lg"
                onClick={handleDownloadAllImages}
                isLoading={isProcessing}
                loadingText={t('preparingDownload')}
                leftIcon={<Box>📦</Box>}
              >
{t('downloadAll')} ({gallery.images.length})
              </Button>
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default ClientGalleryPage;